import React, { useEffect, useState, useMemo } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchDataDetail, usePutDetail } from "@hooks/useFetchDetail";
import { useAuth } from "@context/auth";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import ButtonCst from "@components/ButtonCst";
import { InputCst } from "@components/InputCst";

const CustomFieldsForm: React.FC = () => {
  const { t } = useTranslation(["skillbook-custom-fields"]);
  const { id } = useParams();
  const { user } = useAuth();
  const [formValues, setFormValues] = useState<any>({});

  const { dataDetail: customFields, mutate } = useFetchDataDetail(
    `${ServicesURL.getSkillbookCustomFields}/${id}`,
  );

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
    customContent?: JSX.Element;
  }>();

  const navigate = useNavigate();

  const { trigger: triggerField } = usePutDetail(
    `${ServicesURL.putSkillbookCustomFields}`,
  );

  const dynamicColumns = useMemo(() => {
    if (!customFields || !Array.isArray(customFields)) return [];

    const columns = customFields.map((field: any, index: number) => ({
      accessorKey: field.fieldId,
      header: `${t("header")} ${index + 1}`,
      type: "text",
      id: field.fieldId,
      fieldId: field.fieldId,
      fieldName: field.fieldName,
    }));

    return columns;
  }, [customFields]);

  // Inizializza formValues quando customFields viene caricato o aggiornato
  useEffect(() => {
    if (customFields && Array.isArray(customFields)) {
      const initialValues: any = {};
      customFields.forEach((field: any) => {
        const key = field.fieldId;
        initialValues[key] = field.fieldName || "";
      });
      setFormValues(initialValues);
    }
  }, [customFields]);

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: t("loading") });

    const emptyFields: string[] = [];

    // TODO: Validazione dei campi
    customFields?.forEach((field: any) => {
      const inputKey = field.fieldId;
      const inputValue = inputForm[inputKey];
      const isEmpty =
        !inputValue ||
        (typeof inputValue === "string" && inputValue.trim() === "");

      if (isEmpty) {
        emptyFields.push(field.fieldName);
      }
    });

    try {
      // fieldId non viene inviato se è un nuovo campo, altrimenti viene passato per identificare il campo da aggiornare
      const dataToSubmit = customFields.map((field: any) => {
        const obj: any = {
          corporateId: Number(id),
          fieldName: inputForm[field.fieldId],
        };
        if (field.fieldId >= 0) {
          obj.fieldId = field.fieldId;
        }

        return obj;
      });

      // Se la chiamata va a buon fine ma ci sono errori di validazione sui singoli campi, li mostriamo all'utente
      const response = await triggerField(dataToSubmit as any);

      const errors = response?.length
        ? response.filter((item: any) => {
            return !!item.errorMsg ? item : null;
          })
        : [];
      const contextMessage = (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Problemi con i seguenti campi:
          </Typography>
          {errors &&
            errors.map((item: any, index: number) => (
              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                • {item.fieldId}: {item.errorMsg}s
              </Typography>
            ))}
        </Box>
      );
      const message = errors.length > 0 ? contextMessage : <></>;
      setOpenToast({ s: StatusEnum.OK, message: "", customContent: message });
      mutate();
    } catch (error) {
      setOpenToast({
        s: StatusEnum.KO,
        message: t("error"),
      });
      console.error(error);
    }
  };

  const handleAddField = () => {
    const newFieldId = -(customFields?.length || 0) - 1;
    const newField = {
      fieldId: newFieldId,
      fieldName: "",
      corporateId: id,
    };

    const updatedCustomFields = [...(customFields || []), newField];

    setFormValues({
      ...formValues,
      [newFieldId]: "",
    });

    mutate(updatedCustomFields, false);
  };

  let readOnly: boolean = false;
  if (
    !user?.permissions.find(
      (permission) => permission === "usermgmt.corporates.custom_fields.write",
    )
  ) {
    readOnly = true;
  }

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corporate.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <Box sx={{ mt: "25px" }}>
          <Grid container>
            <Grid item xs={0} lg={4} xl={3}>
              <BackgroundImageCst
                image="section-internal-form.jpg"
                disableGradient
              />
            </Grid>
            <Grid item xs={12} lg={8} xl={9}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  padding: "32px",
                  minHeight: "495px",
                }}
              >
                <Grid container spacing={3}>
                  {dynamicColumns.map((col: any) => (
                    <Grid key={col.accessorKey} item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <InputCst
                            id={col.accessorKey}
                            label={col.header}
                            value={formValues[col.accessorKey] || ""}
                            onChange={(e: any) => {
                              setFormValues({
                                ...formValues,
                                [col.accessorKey]: e.target.value,
                              });
                            }}
                            disabled={readOnly}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      mt: "32px",
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      justifyContent: "space-between",
                    }}
                  >
                    <ButtonCst
                      id="back-button"
                      variant="outlined"
                      size="medium"
                      sx={{ minWidth: "150px", mr: "auto" }}
                      onClick={() =>
                        navigate(`/corporate/${id}/detail/badge-certificates`)
                      }
                    >
                      {t("backButton")}
                    </ButtonCst>
                    {!readOnly && (
                      <ButtonCst
                        id="add-field-btn"
                        onClick={handleAddField}
                        variant="outlined"
                        startIcon={<AddIcon />}
                      >
                        {t("add_custom_field")}
                      </ButtonCst>
                    )}
                    <ButtonCst
                      id="save-button"
                      variant="contained"
                      size="medium"
                      sx={{ minWidth: "150px" }}
                      onClick={() => onSubmit(formValues)}
                      disabled={readOnly}
                    >
                      {t("saveButton")}
                    </ButtonCst>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        removeButtons={openToast?.s !== StatusEnum.CUSTOM}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default CustomFieldsForm;
